import React, { memo } from 'react';
import { Icon } from 'Components/Icons';

import { Label } from 'Components/Label';
import { TextBox } from 'Components/TextBox';
import { PurpleButton } from 'Components/Button';
import { ValidateBackGround } from 'Components/Validation';
import { AtmosphericLogDropzone } from 'Containers/RocketDry';
import { CheckBox } from 'Components/CheckBox';

import { areEqual } from 'Utils/equalityChecks';

import formClasses from 'Themes/form/form.module.css';
import classes from './addLogModal.module.css';

interface Props {
  // modal variable
  formErrors: any;
  isProcessing: boolean;

  // moisture log variables
  material?: any;
  goalAverage?: string;
  latestAverage?: string;
  removed?: boolean;
  photo?: File;

  // modal functions
  onAddReadingButtonClick: (e: any) => void;
  onRemovedCheckboxClick: (e: any) => void;
  closeModal: (e: any) => void;

  // moisture log functions
  onChangeGoalAverage: (e: any) => void;
  onChangeLatestAverage: (e: any) => void;
  setPhoto: any; // needs to be this type or else the dropzone component will error
}

function AddNewMaterialReadingScreen({
  formErrors,
  isProcessing,
  material,
  goalAverage,
  latestAverage,
  removed,
  photo,
  onAddReadingButtonClick,
  closeModal,
  onChangeGoalAverage,
  onChangeLatestAverage,
  onRemovedCheckboxClick,
  setPhoto,
}: Props) {
  const canSubmit = goalAverage && (latestAverage || removed);
  return (
    <form
      className={`requires-validation g-3 ${formClasses.formBase} ${classes.container}`}
      noValidate
      action="#"
      onSubmit={() => {}}
    >
      <div className={classes.formWrapper}>
        <div>
          <div>
            Set your drying goal and record the current dryness of <b>{material?.name}</b>
          </div>
          <br />
          <div className={classes.photoUploadContainer}>
            {photo && (
              <div>
                <img src={URL.createObjectURL(photo)} alt="meter readings" />
                <Icon type="closecircle" className={classes.deleteImageIcon} onClick={() => setPhoto(null)} />
              </div>
            )}
            {!photo && <AtmosphericLogDropzone setPhoto={setPhoto} />}
          </div>
          <ValidateBackGround isValid={!formErrors?.goalAverage?.length}>
            <Label ariaLabel="GoalAverage" className={formClasses.label} htmlFor="goalAverage">
              Goal Average
            </Label>
            <TextBox
              value={goalAverage}
              name="goalAverage"
              type="text"
              className={`mb-0 pb-0 ${formClasses.validateField} ${
                formErrors?.goalAverage?.length ? formClasses.invalidField : formClasses.validField
              } ${formErrors?.goalAverage?.length ? 'is-invalid' : ''}`}
              placeholder=""
              ariaLabel="Please enter the goal average"
              onChange={onChangeGoalAverage}
              autoComplete="off"
            />
            <div className={`${formClasses.invalidFieldFeedback} invalid-feedback`}>{formErrors.goalAverage}</div>
          </ValidateBackGround>
        </div>
        {!removed && (
          <div>
            <ValidateBackGround isValid={!formErrors?.latestAverage?.length}>
              <Label ariaLabel="Latest Average" className={formClasses.label} htmlFor="latestAverage">
                Latest Average
              </Label>
              <TextBox
                value={latestAverage}
                name="latest average"
                type="text"
                className={`mb-0 pb-0 ${formClasses.validateField} ${
                  formErrors?.goalAverage?.length ? formClasses.invalidField : formClasses.validField
                } ${formErrors?.goalAverage?.length ? 'is-invalid' : ''}`}
                placeholder=""
                ariaLabel="Please enter the latest average"
                onChange={onChangeLatestAverage}
                autoComplete="off"
              />
              <div className={`${formClasses.invalidFieldFeedback} invalid-feedback`}>{formErrors.latestAverage}</div>
            </ValidateBackGround>
          </div>
        )}
        <div>
          <CheckBox checked={removed} className={classes.checkbox} onChange={onRemovedCheckboxClick} /> Material Removed
        </div>
        <div className={`modal-footer ${classes.modalFooter}`}>
          <PurpleButton onClick={closeModal} className={classes.cancelButton}>
            Cancel
          </PurpleButton>
          <PurpleButton
            onClick={onAddReadingButtonClick}
            className={classes.saveButton}
            disabled={!canSubmit || isProcessing}
          >
            Save
          </PurpleButton>
        </div>
      </div>
    </form>
  );
}

AddNewMaterialReadingScreen.defaultProps = {
  material: undefined,
  goalAverage: undefined,
  latestAverage: undefined,
  removed: undefined,
  photo: undefined,
};

const AddNewMaterialReadingScreenMemo = memo(AddNewMaterialReadingScreen, areEqual);

export { AddNewMaterialReadingScreenMemo as AddNewMaterialReadingScreen };
